import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';

import { ColumnType } from '../types/ColumnTypes';

import ContentWrapper from './ContentWrapper';

export declare type GetRowKey<RecordType> = (
  record: RecordType,
  index: number,
) => Key;

export declare type Key = React.Key;

interface TableRowProps<RecordType> {
  record: RecordType;
  index: number;
  columns: ColumnType<RecordType>[];
  rowKey: GetRowKey<RecordType>;
  responsive?: boolean;
  getRowLink?: (record: RecordType) => string;
  pageUrl?: string;
  rowColorFunction?: (record: RecordType) => string;
  rowHoverable: boolean;
  cellClassName?: string;
  expandableCellClassName?: string;
  expandable?: TableProps<RecordType>['expandable'];
  expanded: boolean;
  onExpandedChange: (record: RecordType, index: number) => void;
}

export interface TableProps<RecordType> {
  rows?: RecordType[] | null | undefined;
  columns: ColumnType<RecordType>[];
  rowKey: GetRowKey<RecordType>;
  responsive?: boolean;
  loading?: boolean;
  groups?: Array<{ title: string; rows: RecordType[] }>;
  getRowLink?: (record: RecordType) => string;
  customHeader?: React.ReactNode;
  customTopRow?: React.ReactNode;
  customLastRow?: React.ReactNode;
  rowColorFunction?: (record: RecordType) => string;
  rowHoverable?: boolean;
  cellClassName?: string;
  expandableCellClassName?: string;
  expandable?: {
    expandedRowRender: (record: RecordType, index: number) => React.ReactNode;
    rowExpandable?: (record: RecordType) => boolean;
  };
}

const TableRow = <RecordType,>({
  record,
  index,
  columns,
  rowKey,
  responsive,
  getRowLink,
  pageUrl,
  rowColorFunction,
  rowHoverable,
  cellClassName,
  expandableCellClassName,
  expandable,
  expanded,
  onExpandedChange,
}: TableRowProps<RecordType>) => {
  const { t } = useTranslation();

  const getRowColor = (record: RecordType) => {
    if (rowColorFunction) {
      return rowColorFunction(record);
    }
  };

  const isExpandable = expandable?.rowExpandable?.(record) ?? !!expandable;

  return (
    <>
      <tr
        className={`${getRowColor(record)} relative ${
          rowHoverable
            ? 'filter:brightness-50 hover:bg-gray-100 hover-shadow ease-in-out transition-all ease-in-duration-200'
            : ''
        } border-solid border-gray-100 border-0 border-b-2`}
        key={rowKey(record, index) ?? index}>
        {columns?.map(
          (
            {
              render,
              width,
              notLinked,
              onClick,
              contentClassName,
              cellClassName: columnCellClassName,
            },
            columnIndex,
          ) => (
            <td
              key={columnIndex}
              className={`border-b border-gray-200 overflow-hidden ${
                columnCellClassName ?? cellClassName ?? ''
              }`}
              style={{
                width,
                maxWidth: responsive ? 0 : 'auto',
              }}>
              <ContentWrapper
                record={record}
                index={index}
                getRowLink={getRowLink}
                pageUrl={pageUrl}
                render={render}
                notLinked={notLinked}
                onClick={onClick}
                contentClassName={contentClassName}
              />
            </td>
          ),
        )}
        {expandable && (
          <td
            className={`w-12 border-b border-gray-200 text-center ${
              expandableCellClassName ?? cellClassName ?? ''
            }`}
            style={{ maxWidth: 48, minWidth: 48, width: 48 }}>
            {isExpandable && (
              <Button
                aria-expanded={expanded}
                className="h-8 w-8 p-0 text-lg"
                icon={expanded ? <UpOutlined /> : <DownOutlined />}
                type="text"
                onClick={() => onExpandedChange(record, index)}>
                <span className="sr-only">{t('common.toggleRowDetails')}</span>
              </Button>
            )}
          </td>
        )}
      </tr>
      {expanded && isExpandable && expandable && (
        <tr>
          <td className="bg-gray-100 p-4" colSpan={columns.length + 1}>
            {expandable.expandedRowRender(record, index)}
          </td>
        </tr>
      )}
    </>
  );
};

const Table = <RecordType,>({
  rows,
  columns,
  rowKey,
  responsive,
  getRowLink,
  groups,
  customHeader,
  customTopRow,
  customLastRow,
  rowColorFunction,
  rowHoverable = true,
  cellClassName,
  expandableCellClassName,
  expandable,
}: TableProps<RecordType>) => {
  const location = useLocation();
  const [expandedRows, setExpandedRows] = React.useState<Set<Key>>(new Set());

  const _groups: Array<{
    title: string | null;
    rows: RecordType[];
  }> = groups ?? [{ title: null, rows: rows as RecordType[] }];

  const onExpandedChange = (record: RecordType, index: number) => {
    const key = rowKey(record, index);
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col">
      <div className="py-2 px-2 -mx-2">
        <div className="align-middle inline-block w-full shadow-md rounded-md border-b border-gray-200">
          <table className="w-full table-fixed">
            {customHeader == null ? (
              <thead>
                <tr>
                  {columns?.map(({ title, width }, index) => (
                    <th
                      key={index}
                      className="px-6 py-3 border-b border-gray-200 bg-primary-800 text-left text-xs leading-4 font-medium text-primary-100 uppercase tracking-wider overflow-hidden"
                      style={{
                        width,
                        maxWidth: responsive ? 0 : 'auto',
                      }}>
                      {title}
                    </th>
                  ))}
                  {expandable && (
                    <th
                      className="w-12 border-b border-gray-200 bg-primary-800 px-3 py-3"
                      style={{ maxWidth: 48, minWidth: 48, width: 48 }}
                    />
                  )}
                </tr>
              </thead>
            ) : (
              <>{customHeader}</>
            )}
            <tbody className="bg-white">
              {customTopRow && <>{customTopRow}</>}

              {_groups ? (
                _groups.map((group) => (
                  <React.Fragment key={group.title}>
                    {group.title && (
                      <tr>
                        <td
                          className="px-6 pt-4"
                          colSpan={columns.length + (expandable ? 1 : 0)}>
                          <div className="pb-2 text-gray-800 text-base border-solid border-gray-200 border-0 border-b-2">
                            {group.title}
                          </div>
                        </td>
                      </tr>
                    )}
                    {group.rows?.map((record, index) => (
                      <TableRow
                        key={rowKey(record, index) ?? index}
                        record={record}
                        index={index}
                        columns={columns}
                        rowKey={rowKey}
                        responsive={responsive}
                        getRowLink={getRowLink}
                        pageUrl={location.pathname}
                        rowColorFunction={rowColorFunction}
                        rowHoverable={rowHoverable}
                        cellClassName={cellClassName}
                        expandableCellClassName={expandableCellClassName}
                        expandable={expandable}
                        expanded={expandedRows.has(rowKey(record, index))}
                        onExpandedChange={onExpandedChange}
                      />
                    ))}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + (expandable ? 1 : 0)}>
                    <div className="h-16 flex items-center justify-center">
                      <Spin />
                    </div>
                  </td>
                </tr>
              )}

              {customLastRow && <>{customLastRow}</>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Table;
